import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence, useReducedMotion, useMotionValue, useAnimationFrame } from 'framer-motion';
import { UserRound, UsersRound } from 'lucide-react';
type Audience = 'personal' | 'team';
type GalleryCard = {
  id: string;
  category: string;
  title: string;
  quote: string;
  gradient: string;
  icon: string;
  ui: React.ReactNode;
};
// ── FOR YOU ────────────────────────────────────────────────────────────────
// Personal life admin. Titles are the OUTCOME (the thing that ends up done),
// not the chore. Each quote is a real, specific ask in your own words.
const PERSONAL_CARDS: GalleryCard[] = [{
  id: 'travel-booked',
  category: 'Travel',
  title: 'Trip, fully booked',
  quote: '"Book my Friday trip to SF — window seat under $400, a hotel near the office, and put it all on my calendar."',
  gradient: 'from-[#FBF4EC] to-[#F4E7D6]',
  icon: 'google-calendar',
  ui: <div className="bg-white/90 backdrop-blur-sm rounded-xl p-4 shadow-sm w-48 border border-white/40" data-id="element-342">
        <div className="flex items-center justify-between mb-3" data-id="element-343">
          <span className="text-[10px] font-bold text-neutral-800" data-id="element-344">
            SFO · Fri
          </span>
          <span className="text-[10px] font-bold text-green-600" data-id="element-345">$382</span>
        </div>
        <div className="flex items-center gap-2 mb-2" data-id="element-346">
          <div className="h-1.5 w-6 bg-neutral-300 rounded" data-id="element-347" />
          <div className="flex-1 h-px bg-neutral-200" data-id="element-348" />
          <div className="h-1.5 w-6 bg-neutral-300 rounded" data-id="element-349" />
        </div>
        <div className="h-1.5 w-2/3 bg-neutral-100 rounded" data-id="element-350" />
      </div>
}, {
  id: 'errands-handled',
  category: 'Errands',
  title: 'Errands, all done',
  quote: '"Reorder my usual groceries, renew my car registration, and reschedule the dentist for next week. Tell me when each is done."',
  gradient: 'from-[#FBF1E7] to-[#FCE2CC]',
  icon: 'amazon',
  ui: <div className="bg-white/90 backdrop-blur-sm rounded-xl p-4 shadow-sm w-48 border border-white/40" data-id="element-351">
        <div className="h-2 w-20 bg-neutral-800 rounded mb-4" data-id="element-352" />
        <div className="space-y-2.5" data-id="element-353">
          {[true, true, false].map((done, i) => <div key={i} className="flex items-center gap-2" data-id="element-354">
              <div className={`w-3 h-3 rounded-sm shrink-0 ${done ? 'bg-[#EA580C]' : 'border border-neutral-300'}`} data-id="element-355" />
              <div className="h-1.5 w-full bg-neutral-200 rounded" data-id="element-356" />
            </div>)}
        </div>
      </div>
}, {
  id: 'bills-paid',
  category: 'Finance',
  title: 'Bills, never late',
  quote: '"Watch my accounts, pay every recurring bill on time, and flag anything that looks off before it posts."',
  gradient: 'from-[#FBF4EC] to-[#F3E9DB]',
  icon: 'stripe',
  ui: <div className="bg-white/90 backdrop-blur-sm rounded-xl p-4 shadow-sm w-48 border border-white/40" data-id="element-357">
        <div className="flex items-center justify-between mb-3" data-id="element-358">
          <span className="text-[10px] font-bold text-neutral-800" data-id="element-359">
            This month
          </span>
          <span className="text-[10px] font-bold text-green-600" data-id="element-360">On track</span>
        </div>
        <div className="space-y-2" data-id="element-361">
          {[true, true, true].map((paid, i) => <div key={i} className="flex items-center gap-2" data-id="element-362">
              <div className="w-3 h-3 rounded-full bg-[#EA580C] shrink-0" data-id="element-363" />
              <div className="h-1.5 flex-1 bg-neutral-100 rounded" data-id="element-364" />
            </div>)}
        </div>
      </div>
}, {
  id: 'inbox-zero',
  category: 'Inbox',
  title: 'Inbox, cleared by morning',
  quote: '"Triage my personal inbox overnight — unsubscribe from junk, draft replies I just need to approve, and surface only what matters."',
  gradient: 'from-[#FBF1E7] to-[#FCE2CC]',
  icon: 'gmail',
  ui: <div className="bg-white/90 backdrop-blur-sm rounded-xl p-4 shadow-sm w-48 border border-white/40" data-id="element-365">
        <div className="flex items-center justify-between mb-3" data-id="element-366">
          <span className="text-[10px] font-bold text-neutral-800" data-id="element-367">Inbox</span>
          <span className="text-[10px] font-bold text-[#EA580C]" data-id="element-368">
            2 to read
          </span>
        </div>
        <div className="space-y-2" data-id="element-369">
          {[1, 2].map(i => <div key={i} className="flex items-center gap-2" data-id="element-370">
              <div className="w-2 h-2 rounded-full bg-[#EA580C]/40 shrink-0" data-id="element-371" />
              <div className="h-1.5 flex-1 bg-neutral-100 rounded" data-id="element-372" />
            </div>)}
        </div>
      </div>
}, {
  id: 'gift-found',
  category: 'Ordering',
  title: 'The right gift, ordered',
  quote: "\"It's my mom's birthday next week — find something she'd love under $80, order it, and have it shipped to arrive on time.\"",
  gradient: 'from-[#FBF4EC] to-[#F4E7D6]',
  icon: 'amazon',
  ui: <div className="bg-white/90 backdrop-blur-sm rounded-xl p-4 shadow-sm w-48 border border-white/40" data-id="element-373">
        <div className="h-16 w-full rounded-lg bg-neutral-100 mb-3 flex items-center justify-center" data-id="element-374">
          <div className="w-8 h-8 rounded-md bg-[#EA580C]/20" data-id="element-375" />
        </div>
        <div className="flex items-center justify-between" data-id="element-376">
          <div className="h-1.5 w-16 bg-neutral-200 rounded" data-id="element-377" />
          <span className="text-[10px] font-bold text-green-600" data-id="element-378">
            Arrives Tue
          </span>
        </div>
      </div>
}, {
  id: 'plans-made',
  category: 'Scheduling',
  title: 'Plans, locked in',
  quote: '"Find a great dinner spot for four near downtown Friday at 7, book it, and text everyone the details."',
  gradient: 'from-[#FBF1E7] to-[#FBDFC4]',
  icon: 'google-calendar',
  ui: <div className="bg-white/90 backdrop-blur-sm rounded-xl p-4 shadow-sm w-48 border border-white/40" data-id="element-379">
        <div className="flex items-center justify-between mb-3" data-id="element-380">
          <span className="text-[10px] font-bold text-neutral-800" data-id="element-381">
            Fri · 7:00
          </span>
          <span className="text-[10px] font-bold text-green-600" data-id="element-382">
            Table for 4
          </span>
        </div>
        <div className="h-1.5 w-full bg-neutral-100 rounded mb-1.5" data-id="element-383" />
        <div className="h-1.5 w-2/3 bg-neutral-100 rounded" data-id="element-384" />
      </div>
}];
// ── FOR YOUR TEAM ────────────────────────────────────────────────────────────
// Work outcomes a small team can't always staff for. Titles are the delivered
// result; quotes are how a teammate would actually ask in Slack.
const TEAM_CARDS: GalleryCard[] = [{
  id: 'prep-sales-call',
  category: 'Sales',
  title: 'Sales calls, prepped',
  quote: '"Prep the team for tomorrow\'s Acme call — pull the HubSpot deal, last email threads, and a one-pager on where it stands."',
  gradient: 'from-[#FBF1E7] to-[#FCE2CC]',
  icon: 'hubspot',
  ui: <div className="bg-white/90 backdrop-blur-sm rounded-xl p-4 shadow-sm w-48 border border-white/40" data-id="element-385">
        <div className="h-2 w-16 bg-neutral-200 rounded mb-3" data-id="element-386" />
        <div className="flex justify-between items-center mb-2" data-id="element-387">
          <span className="text-[10px] font-bold text-neutral-800" data-id="element-388">
            Acme Corp
          </span>
          <span className="text-[10px] font-bold text-green-600" data-id="element-389">$84k ARR</span>
        </div>
        <div className="h-1.5 w-full bg-neutral-100 rounded mb-1.5" data-id="element-390" />
        <div className="h-1.5 w-3/4 bg-neutral-100 rounded" data-id="element-391" />
      </div>
}, {
  id: 'board-decks',
  category: 'Finance',
  title: 'Board deck, investor-ready',
  quote: '"Refresh the board deck with the latest numbers — MRR from Stripe, burn from QuickBooks, pipeline from Salesforce."',
  gradient: 'from-[#FBF1E7] to-[#FBDFC4]',
  icon: 'stripe',
  ui: <div className="bg-white/90 backdrop-blur-sm rounded-xl p-4 shadow-sm w-48 border border-white/40" data-id="element-392">
        <div className="flex justify-between items-center mb-3" data-id="element-393">
          <span className="text-[10px] font-bold text-neutral-800" data-id="element-394">
            MRR Growth
          </span>
          <span className="text-[10px] font-bold text-green-600" data-id="element-395">+18%</span>
        </div>
        <div className="h-16 w-full flex items-end gap-1" data-id="element-396">
          {[40, 55, 45, 70, 65, 85, 100].map((h, i) => <div key={i} className="flex-1 bg-[#EA580C]/25 rounded-t-sm" style={{
        height: `${h}%`
      }} data-id="element-397" />)}
        </div>
      </div>
}, {
  id: 'pipeline-current',
  category: 'CRM',
  title: 'CRM, always current',
  quote: '"After every call and email, update the deal stage, log the notes, and create the follow-up tasks in Salesforce."',
  gradient: 'from-[#FBF4EC] to-[#F4E7D6]',
  icon: 'salesforce',
  ui: <div className="bg-white/90 backdrop-blur-sm rounded-xl p-4 shadow-sm w-48 border border-white/40" data-id="element-398">
        <div className="h-2 w-20 bg-neutral-800 rounded mb-3" data-id="element-399" />
        <div className="space-y-2" data-id="element-400">
          {['Qualified', 'Proposal', 'Won'].map((_, i) => <div key={i} className="flex items-center gap-2" data-id="element-401">
              <div className="w-3 h-3 rounded-full bg-[#EA580C] shrink-0" data-id="element-402" />
              <div className="h-1.5 flex-1 bg-neutral-100 rounded" data-id="element-403" />
            </div>)}
        </div>
      </div>
}, {
  id: 'reports-shipped',
  category: 'Reporting',
  title: 'Reports, shipped on schedule',
  quote: '"Every Monday at 8am, compile last week\'s metrics into the standup doc and post the summary to #leadership."',
  gradient: 'from-[#FBF4EC] to-[#F3E9DB]',
  icon: 'slack',
  ui: <div className="bg-white/90 backdrop-blur-sm rounded-xl p-4 shadow-sm w-48 border border-white/40" data-id="element-404">
        <div className="flex items-center justify-between mb-3" data-id="element-405">
          <span className="text-[10px] font-bold text-neutral-800" data-id="element-406">
            Weekly report
          </span>
          <span className="text-[10px] font-bold text-green-600" data-id="element-407">Sent</span>
        </div>
        <div className="h-1.5 w-full bg-neutral-100 rounded mb-1.5" data-id="element-408" />
        <div className="h-1.5 w-5/6 bg-neutral-100 rounded mb-1.5" data-id="element-409" />
        <div className="h-1.5 w-2/3 bg-neutral-100 rounded" data-id="element-410" />
      </div>
}, {
  id: 'tickets-triaged',
  category: 'Support',
  title: 'Support queue, triaged',
  quote: '"Sort the support queue — tag by urgency, draft replies for the routine ones, and escalate anything at risk of breaching SLA."',
  gradient: 'from-[#FBF1E7] to-[#FCE2CC]',
  icon: 'zendesk',
  ui: <div className="bg-white/90 backdrop-blur-sm rounded-xl p-4 shadow-sm w-48 border border-white/40" data-id="element-411">
        <div className="flex items-center justify-between mb-3" data-id="element-412">
          <span className="text-[10px] font-bold text-neutral-800" data-id="element-413">Open</span>
          <span className="text-[10px] font-bold text-[#EA580C]" data-id="element-414">4 urgent</span>
        </div>
        <div className="space-y-2" data-id="element-415">
          {[1, 2, 3].map(i => <div key={i} className="flex items-center gap-2" data-id="element-416">
              <div className="w-2 h-2 rounded-full bg-[#EA580C]/40 shrink-0" data-id="element-417" />
              <div className="h-1.5 flex-1 bg-neutral-100 rounded" data-id="element-418" />
            </div>)}
        </div>
      </div>
}, {
  id: 'new-hires-ramped',
  category: 'Onboarding',
  title: 'New hires, ramped in week one',
  quote: '"Set up our new AE — build their onboarding doc, grant tool access, and queue intro messages to the accounts they\'re inheriting."',
  gradient: 'from-[#FBF4EC] to-[#F4E7D6]',
  icon: 'notion',
  ui: <div className="bg-white/90 backdrop-blur-sm rounded-xl p-4 shadow-sm w-48 border border-white/40" data-id="element-419">
        <div className="h-2 w-24 bg-neutral-800 rounded mb-4" data-id="element-420" />
        <div className="space-y-2.5" data-id="element-421">
          {[true, true, false].map((done, i) => <div key={i} className="flex items-center gap-2" data-id="element-422">
              <div className={`w-3 h-3 rounded-sm shrink-0 ${done ? 'bg-[#EA580C]' : 'border border-neutral-300'}`} data-id="element-423" />
              <div className="h-1.5 w-full bg-neutral-200 rounded" data-id="element-424" />
            </div>)}
        </div>
      </div>
}];
function AppIcon({
  slug,
  className
}: {
  slug: string;
  className?: string;
}) {
  return <img src={`https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/${slug}.svg`} alt="" aria-hidden="true" className={className} onError={e => {
    const img = e.currentTarget as HTMLImageElement;
    if (!img.src.includes('simpleicons')) {
      img.src = `https://cdn.simpleicons.org/${slug.replace(/-/g, '')}`;
    } else {
      img.style.visibility = 'hidden';
    }
  }} data-id="element-425" />;
}
function GalleryJobCard({
  card,
  audience
}: {
  card: GalleryCard;
  audience: Audience;
}) {
  const isTeam = audience === 'team';
  return <div className="w-[280px] md:w-[320px] shrink-0 flex flex-col gap-6" data-id="element-426">
      {/* Visual Area */}
      <div className={`h-[340px] rounded-3xl bg-gradient-to-br ${card.gradient} p-6 relative overflow-hidden flex flex-col items-center justify-center`} data-id="element-427">
        <div className="relative z-10 flex flex-col items-center" data-id="element-428">
          {card.ui}
          <div className="w-px h-12 bg-black/10 my-2" data-id="element-429" />
          <div className="w-10 h-10 rounded-full bg-white/80 backdrop-blur-md shadow-sm border border-white flex items-center justify-center" data-id="element-430">
            <AppIcon slug={card.icon} className="w-5 h-5 object-contain" data-id="element-431" />
          </div>
        </div>
        <div className="absolute inset-0 opacity-[0.03] mix-blend-overlay pointer-events-none" style={{
        backgroundImage: 'url("https://grainy-gradients.vercel.app/noise.svg")'
      }} data-id="element-432" />
      </div>

      {/* Text Area */}
      <div className="flex flex-col gap-3 px-2" data-id="element-433">
        <span className="flex items-center gap-2 font-mono text-[10px] tracking-[0.22em] uppercase" data-id="element-434">
          <span className="w-1.5 h-1.5 rounded-full bg-[#EA580C]" data-id="element-435" />
          <span className="text-[#EA580C]" data-id="element-436">{card.category}</span>
        </span>
        <h3 className="text-xl font-bold text-[#2B211A] -mt-1" data-id="element-437">{card.title}</h3>
        <p className="text-sm text-neutral-600 leading-relaxed" data-id="element-438">{card.quote}</p>
      </div>
    </div>;
}
function CardMarquee({
  cards,
  audience,
  reduceMotion
}: {
  cards: GalleryCard[];
  audience: Audience;
  reduceMotion: boolean | null;
}) {
  if (reduceMotion) {
    return <div className="flex overflow-x-auto gap-6 pb-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-id="element-439">
        {cards.map(card => <GalleryJobCard key={card.id} card={card} audience={audience} data-id="element-440" />)}
      </div>;
  }
  return <DraggableMarquee cards={cards} audience={audience} data-id="element-441" />;
}
// Auto-advancing AND draggable marquee. The track auto-scrolls continuously;
// the user can grab and drag it left/right at any point, and it resumes
// auto-scrolling from wherever they release. Three identical copies of the card
// set are rendered so there's always content buffering both sides during a
// drag, and the x position is wrapped into the middle band every frame for a
// seamless infinite loop.
function DraggableMarquee({
  cards,
  audience
}: {
  cards: GalleryCard[];
  audience: Audience;
}) {
  const trackRef = useRef<HTMLDivElement>(null);
  const x = useMotionValue(0);
  const draggingRef = useRef(false);
  // Width of ONE card set (the track holds three identical sets).
  const [setWidth, setSetWidth] = useState(0);
  // Auto-scroll speed (px/sec). Faster on mobile, where the user is more
  // likely to just watch the cards drift by than grab and drag them.
  const [speed, setSpeed] = useState(50);
  // Measure a single set's width, then park x in the middle copy so there's a
  // full set of cards buffering both the left and right during drags.
  useEffect(() => {
    const measure = () => {
      const el = trackRef.current;
      if (!el) return;
      const w = el.scrollWidth / 3;
      setSetWidth(w);
      x.set(-w);
      setSpeed(window.innerWidth < 640 ? 95 : 55);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [cards, x]);
  // Wrap x into the middle band so the loop stays seamless and always filled.
  const wrapMid = (v: number) => {
    if (!setWidth) return v;
    const mod = ((v + 2 * setWidth) % setWidth + setWidth) % setWidth;
    return -2 * setWidth + mod;
  };
  useAnimationFrame((_, delta) => {
    if (draggingRef.current || !setWidth) return;
    // Move the track left→right (x increasing); wrapMid keeps it seamless.
    const next = x.get() + speed * delta / 1000;
    x.set(wrapMid(next));
  });
  return (
    // No edge fade on mobile (the strong left fade looked heavy on narrow
    // screens) — it reads as a clean carousel there. The soft side fades only
    // kick in from the sm breakpoint up.
    <div className="relative w-full overflow-hidden sm:[mask-image:linear-gradient(to_right,transparent,black_6%,black_94%,transparent)]" data-id="element-442">
      <motion.div ref={trackRef} className="flex gap-6 w-max cursor-grab active:cursor-grabbing select-none touch-pan-y" style={{
        x
      }} drag="x" dragMomentum={false} dragElastic={0.04} onDragStart={() => {
        draggingRef.current = true;
      }} onDragEnd={() => {
        // Snap back into the seamless middle band, then let the auto-scroll
        // frame loop take over again from this position.
        x.set(wrapMid(x.get()));
        draggingRef.current = false;
      }} data-id="element-443">
        {[...cards, ...cards, ...cards].map((card, i) => <GalleryJobCard key={`${card.id}-${i}`} card={card} audience={audience} data-id="element-444" />)}
      </motion.div>
    </div>
  );
}
export function AudienceSwitch() {
  const reduceMotion = useReducedMotion();
  const [audience, setAudience] = useState<Audience>('personal');
  const cards = audience === 'team' ? TEAM_CARDS : PERSONAL_CARDS;
  return <section className="w-full bg-[#FAF8F4] py-24 md:py-32 border-b border-neutral-200/70 relative overflow-hidden" data-id="element-445">
      <div className="max-w-[1280px] mx-auto px-6 md:px-10 relative" data-id="element-446">
        {/* Section opening */}
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-80px'
      }} transition={{
        duration: 0.6,
        ease: [0.16, 1, 0.3, 1]
      }} className="mb-10 md:mb-12 max-w-3xl mx-auto text-center" data-id="element-447">
          <span className="block text-xs font-semibold tracking-[5px] uppercase text-neutral-400 mb-5 md:mb-6" data-id="element-448">
            Built for the real world
          </span>
          <h2 className="text-3xl md:text-5xl font-title font-medium text-[#2B211A] tracking-tight leading-[1.1] mb-5 md:mb-6" data-id="element-449">
            One Giga. <span className="text-[#EA580C]" data-id="element-450">Two ways</span> to put it
            to work.
          </h2>
          <p className="text-base md:text-lg text-neutral-500 font-medium leading-relaxed max-w-2xl mx-auto" data-id="element-451">
            The same assistant moves between your business and your personal
            life. Pick a side to see exactly what it gets done.
          </p>
        </motion.div>

        {/* Audience toggle */}
        <div className="flex justify-center mb-12 md:mb-16" data-id="element-452">
          <div role="tablist" aria-label="Choose audience" className="relative grid grid-cols-2 gap-1 p-1.5 w-full max-w-[360px] rounded-full border border-neutral-200/70 bg-white shadow-sm" data-id="element-453">
            <motion.div aria-hidden="true" className="absolute inset-y-1.5 w-[calc(50%-6px)] rounded-full bg-[#FAF8F4] border border-neutral-200/80 shadow-sm" animate={{
            left: audience === 'personal' ? '6px' : 'calc(50%)'
          }} transition={{
            type: 'spring',
            stiffness: 400,
            damping: 30
          }} data-id="element-454" />
            <button role="tab" aria-selected={audience === 'personal'} onClick={() => setAudience('personal')} className={`relative z-10 flex items-center justify-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-colors duration-300 ${audience === 'personal' ? 'text-[#2B211A]' : 'text-neutral-500 hover:text-[#2B211A]'}`} data-id="element-455">
              <UserRound size={16} strokeWidth={2.5} className={audience === 'personal' ? 'text-[#EA580C]' : 'text-neutral-400'} data-id="element-456" />
              <span className="whitespace-nowrap" data-id="element-457">For you</span>
            </button>
            <button role="tab" aria-selected={audience === 'team'} onClick={() => setAudience('team')} className={`relative z-10 flex items-center justify-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-colors duration-300 ${audience === 'team' ? 'text-[#2B211A]' : 'text-neutral-500 hover:text-[#2B211A]'}`} data-id="element-458">
              <UsersRound size={16} strokeWidth={2.5} className={audience === 'team' ? 'text-[#EA580C]' : 'text-neutral-400'} data-id="element-459" />
              <span className="whitespace-nowrap" data-id="element-460">For your team</span>
            </button>
          </div>
        </div>

        {/* Separate carousel per audience */}
        <div className="w-full overflow-hidden" data-id="element-461">
          <AnimatePresence mode="wait" data-id="element-462">
            <motion.div key={audience} initial={{
            opacity: 0,
            y: 12
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -8
          }} transition={{
            duration: 0.35,
            ease: [0.16, 1, 0.3, 1]
          }} data-id="element-463">
              <CardMarquee cards={cards} audience={audience} reduceMotion={reduceMotion} data-id="element-464" />
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>;
}